// MUI から Box、Grid、Typography コンポーネントをインポート
import { Box, Grid, Typography } from '@mui/material';
// React とフックをインポート
import React, { useEffect, useState } from 'react';
// react-hook-form から UseFormReturn フックをインポート
import { UseFormReturn } from 'react-hook-form';
// react-i18next から useTranslation フックをインポート
import { useTranslation } from 'react-i18next';
import RHFDeskTopDatePicker from 'src/components/react-hook-form/RHFDeskTopDatePicker';
import RHFTextField from 'src/components/react-hook-form/RHFTextField';
import { CREATE_USER_SYSTEM } from 'src/constants';
import { usePermission } from 'src/hooks/usePermission';
// Redux ストアセレクターをインポート
import { format, isValid } from 'date-fns';
import useAuth from 'src/hooks/useAuth';
import { RootState, useAppDispatch, useAppSelector } from 'src/redux/store';
import { useValidSaveButton } from '../hooks/useValidSaveButton';
import { setCalendarData } from '../store/enqueteCalendarSlice';
// プロパティインターフェースを定義
export interface CalendarInputProps {
  // フォームのラベルとして使用する文字列
  formLabel: string;
  // react-hook-form のメソッド
  methods: UseFormReturn;
}
// カレンダーを入力するための日本語、英語共通のコンポーネント
export const CalendarInput: React.FC<CalendarInputProps> = (props) => {
  // パラメータ
  const { formLabel, methods } = props;
  // Redux ストアからカレンダーデータを取得
  const calendarData = useAppSelector((state: RootState) => state.enqueteCalendar.calendarData);
  const isConfirmor = useAppSelector((state: RootState) => state.enqueteCalendar.isConfirmor);
  // 翻訳関数を取得
  const { t } = useTranslation();
  // Redux dispatch 関数を取得
  const dispatch = useAppDispatch();
  const MAX_LENGTH_DESCRIPTION = 3000;
  const MAX_LENGTH_TITLE = 200;
  // 権限フックを使用（各種ロールフラグを取得）
  const { isAdmin, isViewerAdmin, isSurveyOwner, isAnswerEditor, isAnswerReader, isRespondentPerson, isRespondentManager } = usePermission();
  const isDisabled = calendarData?.createdBy === CREATE_USER_SYSTEM;
  const [isTextDisabled, setIsTextDisabled] = useState(false);
  const { user } = useAuth();
  useEffect(() => {
    const isSystemCreated = calendarData?.createdBy === CREATE_USER_SYSTEM;
    const isCreatedByMe = calendarData.createdBy === user?.email;

    // 共有権限
    const hasPermission =
      isSurveyOwner || // 管理者
      isAdmin || // admin
      isViewerAdmin || // viewer admin
      isAnswerEditor || // 本部極担当者（編集）
      isRespondentPerson || // 回答者
      isConfirmor || // 確認者
      !!calendarData?.reminderUserId; // リマインドの宛先

    // 手動権限
    const hasManualPermission =
      isAnswerReader || // 本部極担当者（参照）
      isRespondentManager || // 依頼先管理者
      !calendarData?.id || // 新規作成
      isCreatedByMe; // 個人作成

    // 「現地／実物確認の予定日」というタイトルのレコードがシステムによって自動生成される場合、アクセスは特定の権限を持つユーザーに制限されます。
    // それ以外の場合、必要な権限は「基本権限 + isAnswerReader」（本部部門担当者 – 閲覧のみ）となります。
    const canEditText = isSystemCreated ? hasPermission && calendarData.titleJpn === '現地現物確認予定日' : hasPermission || hasManualPermission;

    setIsTextDisabled(!canEditText);
  }, [calendarData, isAdmin, isViewerAdmin, isSurveyOwner, isAnswerEditor, isAnswerReader, isRespondentPerson, isConfirmor]);

  // Validator
  const { startDateRule, endDateRule } = useValidSaveButton(methods);
  // 見出し（日本語）
  const titleJpnToWatch = methods.watch('titleJpn');
  // 見出し（英語）
  const titleEngToWatch = methods.watch('titleEng');
  // 期間開始日
  const startDateToWatch = methods.watch('startDate');
  // 期間終了日
  const endDateToWatch = methods.watch('endDate');
  // 見出し（日本語）
  const descriptionJpnToWatch = methods.watch('descriptionJpn');
  // 見出し（英語）
  const descriptionEngToWatch = methods.watch('descriptionEng');

  //初回実行
  useEffect(() => {
    // 見出し（日本語）
    methods.setValue('titleJpn', calendarData?.titleJpn);
    // 見出し（英語）
    methods.setValue('titleEng', calendarData?.titleEng);
    // 期間開始日
    methods.setValue('startDate', calendarData && calendarData.openAt ? new Date(calendarData.openAt) : null);
    // 期間終了日
    methods.setValue('endDate', calendarData && calendarData.closeAt ? new Date(calendarData.closeAt) : null);
    // 見出し（日本語）
    methods.setValue('descriptionJpn', calendarData?.contentJpn);
    // 見出し（英語）
    methods.setValue('descriptionEng', calendarData?.contentEng);
  }, []);

  useEffect(() => {
    // 見出し（日本語）
    const titleJpn = methods.getValues('titleJpn');
    // 見出し（英語）
    const titleEng = methods.getValues('titleEng');
    // 期間開始日
    const startDate = methods.getValues('startDate');
    let openAt: any;
    if (startDate) {
      if (!isValid(startDate)) {
        return;
      }
      const startDateZone = new Date(new Date(format(startDate, 'yyyy/MM/dd'))?.getTime() - new Date().getTimezoneOffset() * 60 * 1000);
      if (startDate.getTime() === startDateZone.getTime()) {
        openAt = startDate;
      } else {
        openAt = startDateZone;
      }
    }

    // 期間終了日
    const endDate = methods.getValues('endDate');
    let closeAt: any;
    if (endDate) {
      if (!isValid(endDate)) {
        return;
      }
      const endDateZone = new Date(new Date(format(endDate, 'yyyy/MM/dd'))?.getTime() - new Date().getTimezoneOffset() * 60 * 1000);
      if (endDate.getTime() === endDateZone.getTime()) {
        closeAt = endDate;
      } else {
        closeAt = endDateZone;
      }
    }
    // 見出し（日本語）
    const descriptionJpn = methods.getValues('descriptionJpn');
    // 見出し（英語）
    const descriptionEng = methods.getValues('descriptionEng');
    dispatch(
      setCalendarData({
        ...calendarData!,
        titleJpn: titleJpn,
        titleEng: titleEng,
        openAt: openAt,
        closeAt: closeAt,
        contentJpn: descriptionJpn,
        contentEng: descriptionEng,
      }),
    );
    // 画面入力値変更監視
  }, [titleJpnToWatch, titleEngToWatch, startDateToWatch, endDateToWatch, descriptionJpnToWatch, descriptionEngToWatch]);

  return (
    <>
      <Box component={'div'}>
        <Grid container direction="column" rowSpacing={2}>
          <Grid container item direction="row" justifyContent="flex-start">
            {/* コンプライアンス・レビューカレンダー管理 */}
            <Grid item xs={1.15}>
              <Typography variant="subtitle1">{t('CalendarCreate.title')}</Typography>
            </Grid>
            {/* 見出し（日本語） */}
            <Grid item xs={10.85} style={{ display: formLabel === 'calendarJpn' ? 'block' : 'none' }}>
              <RHFTextField
                // フィールド名
                name={'titleJpn'}
                // フォーム制御オブジェクト
                control={methods.control}
                // 表示ラベル
                label={t('CalendarCreate.title')}
                // サイズ
                size="small"
                // 表示スタイル
                variant="outlined"
                // 無効化
                disabled={isDisabled}
                // 入力ヒント
                placeholder={t('CalendarCreate.validateError.titleMaxLength')}
                rules={{
                  maxLength: {
                    value: MAX_LENGTH_TITLE,
                    message: t('CalendarCreate.validateError.titleMaxLength'),
                  },
                }}
                // 幅を100%にする
                fullWidth
              />
            </Grid>
            {/* 見出し（英語） */}
            <Grid item xs={10.85} style={{ display: formLabel !== 'calendarJpn' ? 'block' : 'none' }}>
              <RHFTextField
                // フィールド名
                name={'titleEng'}
                // フォーム制御オブジェクト
                control={methods.control}
                // 表示ラベル
                label={t('CalendarCreate.title')}
                // サイズ
                size="small"
                // 表示スタイル
                variant="outlined"
                // 無効化
                disabled={isDisabled}
                // 入力ヒント
                placeholder={t('CalendarCreate.validateError.titleMaxLength')}
                // バリデーションルール
                rules={{
                  maxLength: {
                    value: MAX_LENGTH_TITLE,
                    message: t('CalendarCreate.validateError.titleMaxLength'),
                  },
                }}
                // 幅を100%にする
                fullWidth
              />
            </Grid>
          </Grid>
          <Grid container item direction="row" justifyContent="flex-start">
            {/* 適用期間 */}
            <Grid item xs={1.15}>
              <Typography variant="subtitle1">{t('CalendarCreate.applicablePeriod')}</Typography>
            </Grid>
            {/* 適用期間開始日 */}
            <Grid item>
              <RHFDeskTopDatePicker
                // フォーム制御オブジェクト
                control={methods.control}
                // 入力マスク
                mask="____/__/__"
                // フィールド名
                name="startDate"
                // 表示ラベル
                label={t('CalendarCreate.startDate')}
                // バリデーションルール
                rules={startDateRule}
                // 日付選択の表示単位:年月日
                views={['year', 'month', 'day']}
                // 日付フォマード
                inputFormat={'yyyy/MM/dd'}
                // 無効化
                disabled={isDisabled}
              />
            </Grid>
            <Grid item sx={{ margin: '8px 16px 0 16px' }}>
              <Typography variant="body1">〜</Typography>
            </Grid>
            {/* 適用期間終了日 */}
            <Grid item>
              <RHFDeskTopDatePicker
                // フォーム制御オブジェクト
                control={methods.control}
                // 入力マスク
                mask="____/__/__"
                // フィールド名
                name="endDate"
                // 表示ラベル
                label={t('CalendarCreate.endDate')}
                // バリデーションルール
                rules={endDateRule}
                // 日付選択の表示単位:年月日
                views={['year', 'month', 'day']}
                // 日付フォマード
                inputFormat={'yyyy/MM/dd'}
                // 無効化
                disabled={isDisabled}
              />
            </Grid>
          </Grid>
          <Grid container item direction="row" justifyContent="flex-start">
            {/* 本文（日本語） */}
            <Grid item xs={12} style={{ display: formLabel === 'calendarJpn' ? 'block' : 'none' }}>
              <RHFTextField
                // フィールド名
                name="descriptionJpn"
                // 表示ラベル
                label={t('CalendarCreate.text')}
                // フォーム制御オブジェクト
                control={methods.control}
                // 行数
                rows={6}
                // 複数行入力
                multiline={true}
                // 幅を100%にする
                fullWidth
                // 入力ヒント
                placeholder={t('CalendarCreate.validateError.textMaxLength')}
                // バリデーションルール
                rules={{
                  maxLength: {
                    value: MAX_LENGTH_DESCRIPTION,
                    message: t('CalendarCreate.validateError.textMaxLength'),
                  },
                }}
                // 無効化
                disabled={isTextDisabled}
              />
            </Grid>
            {/* 本文（英語） */}
            <Grid item xs={12} style={{ display: formLabel !== 'calendarJpn' ? 'block' : 'none' }}>
              <RHFTextField
                // フィールド名
                name="descriptionEng"
                // 表示ラベル
                label={t('CalendarCreate.text')}
                // フォーム制御オブジェクト
                control={methods.control}
                // 行数
                rows={6}
                // 複数行入力
                multiline={true}
                // 幅を100%にする
                fullWidth
                // 入力ヒント
                placeholder={t('CalendarCreate.validateError.textMaxLength')}
                // バリデーションルール
                rules={{
                  maxLength: {
                    value: MAX_LENGTH_DESCRIPTION,
                    message: t('CalendarCreate.validateError.textMaxLength'),
                  },
                }}
                // 無効化
                disabled={isTextDisabled}
              />
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};
